import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from "@angular/core";
import { SessionService } from "./../../Services/session.service";

import { AppSettings } from "../../app-settings";

import { CementingCategory, CementingDifficulty, CementingStatus } from '../../models/cementing-status';
import { deriveJourneyHeader, JourneyHeaderView } from '../../models/cementing-messages';

/**
 * A cementing learning level of the journey view (Cementing Medium / Hard).
 * The header shows the module's status, latest L20 and goal from the
 * Scholaranium status API; the body hosts the cementing panel and the level's
 * activity list. The LMS journey status of the level follows the API (forward
 * only: Not Started → Started → Completed) so the journey progress bar and the
 * course stage keep working without the old manual "Mark complete" button.
 */
@Component({
  selector: 'app-cementing-learning-level',
  templateUrl: './cementing-learning-level.component.html',
  styleUrls: ['./cementing-learning-level.component.css']
})
export class CementingLearningLevelComponent implements OnInit, OnChanges {
  expandIconAssetURL: string = AppSettings.cdnUrl + "assets/images/expand.png";
  collapseIconAssetURL: string = AppSettings.cdnUrl + "assets/images/collapse_level.png";

  @Input() level = {
    grade: "",
    levelName: "",
    collapsed: true,
    activityDetailsCollapsed: true,
    completedActivities: 0,
    totalActivities: 0,
    gradeClass: "",
    totalTimeSpent: 0,
    remainingEffort: 0,
    levelStatus: "",
    startState: "",
    nextActivityName: "",
    overallPerformanceScore: 0,
    activityListInfo: "",
    isRecentModule: "NO",
    courseShortName: ''
  };

  @Input() journeyCourseId;

  /** Course page name (route :page), passed on to the panel for its in-course routes. */
  @Input() pageName: string;

  /** VERBAL | QUANT | DI */
  @Input() section;

  /** Subject short name (journey name), e.g. CR. */
  @Input() subject: string;

  /** Cementing module of this level from the Scholaranium status API; null when the subject has none. */
  @Input() category: CementingCategory | null = null;

  /** The whole subject status (the Hard panel reads Medium's state for its warning). */
  @Input() status: CementingStatus | null = null;

  @Output() collapse_level = new EventEmitter<any>();
  /** "Move to Hard Cementing" / "Go to Medium Cementing" from the panel: the journey opens that level. */
  @Output() open_module = new EventEmitter<CementingDifficulty>();

  header: JourneyHeaderView = deriveJourneyHeader(null);

  status_class = {
    'new': 'grade_NR',
    'started': 'grade_B',
    'completed': 'grade_A'
  };

  constructor(private sessionService: SessionService) {}

  ngOnInit() {
    this.sessionService.getWebinarComponentState.subscribe((state) => {
      if (state === "expanded") {
        this.level.collapsed = true;
      }
    });
  }

  ngOnChanges() {
    this.header = deriveJourneyHeader(this.category);
  }

  /** The level header's status pill: the LMS status while the subject has no cementing module. */
  get statusKey(): 'new' | 'started' | 'completed' {
    if (this.category) { return this.header.status; }
    const lms = (this.level.levelStatus || '').toLowerCase();
    return lms.indexOf('completed') >= 0 ? 'completed' : lms === 'new' || lms === '' ? 'new' : 'started';
  }

  /** Execution-tracker label: the module state when the API serves one, else the LMS status. */
  get statusLabel(): string {
    if (this.category) { return this.header.statusLabel; }
    const key = this.statusKey;
    return key === 'completed' ? 'Completed' : key === 'started' ? 'In progress' : 'Not started';
  }

  toggleLevelDetailsView() {
    this.level.collapsed = !this.level.collapsed;
    if (!this.level.collapsed) {
      this.sessionService.shareLevelCollapsedStatus("expanded");
    }
  }

  /** "Back to course" on a finished module collapses the level. */
  collapseLevel() {
    this.level.collapsed = true;
    this.collapse_level.emit('collapsed');
  }
}
